/** @format */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppSelector } from "../../app/hooks";
import { useParams } from "react-router-dom";
import { useLocale } from "../i18n/hooks";
import {
  selectItemId,
  selectMaxStages,
  selectStage,
  setIsLoading,
  setShowModal,
  setStage,
} from "./contentEditorSlice";
import { produce } from "immer";
import { Form, Modal } from "react-bootstrap";
import { useDispatch } from "react-redux";
import ContentEditorHeader from "./contentEditorHeader";
import { ErrorBoundary } from "../errorBoundary/ErrorBoundary";
import {
  DropItemProps,
  DragItemProps,
} from "../activityCards/cards/SortableColumnsCard/types";
import { Card, CardData, CardObject, Resource, UserResource } from "../resources/types";
import {
  getUserResourceSubLessonCards,
  saveCard as saveUserResourceCard,
} from "../userResources/userResourceFunctions";
import { saveCard as saveResourceCard } from "../resources/ResourceFunctions";
import BLSpinner from "../../assets/animations/BLSpinner/BLSpinner";
import { useTranslation } from "react-i18next";
import { activityCardTypes, staticCardTypes } from "./utils";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useGetUserQuery } from "../../app/services/userApi";

type Props = {
  resource: UserResource;
  dev?: boolean;
};

const AddActivity: React.FC<Props> = ({ resource, dev = false }) => {
  const itemId = useAppSelector(selectItemId());
  const stage = useAppSelector(selectStage());
  const maxStages = useAppSelector(selectMaxStages());
  const locale = useLocale();
  const { t: _ } = useTranslation();

  const dispatch = useDispatch();

  const isAuthenticated =
    useAppSelector((state) => state.auth.userToken?.token) || false;
  const { data: user, isLoading } = useGetUserQuery(
    !!isAuthenticated ? undefined : skipToken,
  );
  const isStaff = !isLoading && user !== undefined && (user.role === "wjec" || user.role === "admin");

  let { lessonId, pageId } = useParams();

  const lesson = resource?.Object.lessons[Number(lessonId)];
  var pageIdNumber = Number(pageId);
  const subLessonUuid = lesson?.subLessons[pageIdNumber]?.uuid ?? "";

  const [card, setCard] = useState<Card>();
  const [type, setType] = useState<string>("");
  const [data, setData] = useState<CardData>({});
  const [validated, setValidated] = useState<boolean>(false);

  const getCard = useCallback(async () => {
    const cards = await getUserResourceSubLessonCards({
      resource: resource.Object,
      locale,
      lessonIndex: Number(lessonId),
      subLessonUuid,
      copy: resource.Copy,
    });
    const card: Card | undefined = cards?.find((item) => item.UUID === itemId);

    setCard(card);
  }, [lessonId, locale, resource, itemId, subLessonUuid]);

  const capitalise = (str: string) =>
    str.charAt(0).toUpperCase() + str.slice(1);

  const ActivityEditType = useCallback((type: string) => {
    let formattedType = capitalise(type);

    switch (formattedType) {
      case "FillBlankDropdown":
        formattedType = "FillBlankText";
        break;
      case "QuestionAndAnswer":
      case "RandomGenerator":
        formattedType = "AnswerQuestion";
        break;
    }

    return formattedType;
  }, []);

  useEffect(() => {
    getCard();
  }, [getCard]);

  useEffect(() => {
    if (card && lessonId && pageId) {
      const cardObject: CardObject = card.Object;
      setType(
        produce(cardObject.type, (draft) => {
          switch (draft) {
            case "questionAndAnswer":
            case "randomGenerator":
              draft = "answerQuestion";
              break;
          }

          return draft;
        }),
      );
      setData(
        produce(cardObject.data, (draft) => {
          if (draft.dropItems && ActivityEditType(type) === "SortableColumns") {
            draft.dropItems.map((column: DropItemProps, index: number) => {
              draft.dragItems
                .filter((item: DragItemProps) => item.answer === column.id)
                .map((item: DragItemProps) => (item.answer = index.toString()));

              column.id = index.toString();

              return column;
            });
          }
          if (
            draft.activityContent &&
            ActivityEditType(type) === "FillBlankText"
          ) {
            draft.activityContent[locale] = (
              draft.activityContent[locale] ?? ""
            )
              .replace(
                /<input\s.*?data-answer=(?:\\)?(?:"|')(.+?)(?:\\)?(?:"|').*?(?:\/)?>/g,
                '<span class="blank-input">$1</span>',
              )
              .replace(
                /<select\s.*?data-answer=(?:\\)?(?:"|')(.+?)(?:\\)?(?:"|').*?><\/select>/g,
                '<span class="blank-dropdown">$1</span>',
              );
          }
          if (
            draft.question &&
            draft.answer &&
            ActivityEditType(type) === "AnswerQuestion"
          ) {
            const { question, answer, feedback, ...newData } = draft;
            return (draft = {
              ...newData,
              questions: [{ question, answer, feedback }],
              showAnswers: true,
              randomiseQuestions: false,
            });
          } else if (
            draft.questions &&
            ActivityEditType(type) === "AnswerQuestion"
          ) {
            return (draft = { ...draft, randomiseQuestions: true });
          }
        }),
      );
    }
  }, [
    resource,
    itemId,
    lessonId,
    card,
    pageId,
    locale,
    type,
    ActivityEditType,
  ]);

  const onChangeData = useCallback((value: CardData) => {
    setData(value);
  }, []);

  const onChangeValidated = useCallback((value: boolean) => {
    setValidated(value);
  }, []);

  const ActivityEditForm = useMemo(
    () =>
      React.lazy(
        () =>
          import(
            `../activityCards/cards/${ActivityEditType(
              type,
            )}Card/edit/add${ActivityEditType(type)}`
          ),
      ),
    [ActivityEditType, type],
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();
    let valid = true;

    if (form.checkValidity() === true) {
      if (maxStages && stage < maxStages) {
        dispatch(setStage(stage + 1));
      } else {
        dispatch(setIsLoading(true));
        if (card && !dev) {
          const _card = produce(card, (draft) => {
            switch (draft.Object.type) {
              case "questionAndAnswer":
              case "randomGenerator":
                draft.Object.type = "answerQuestion";
                break;
            }
            draft.Object.data = data;
          });

          if (resource.PK.includes("USER_BL#")) {
            await saveUserResourceCard(
              _card,
              locale,
              resource,
              Number(lessonId),
              pageIdNumber,
            );
          } else {
            if (isStaff) {
              await saveResourceCard(
                _card,
                resource as Resource,
                Number(lessonId),
                pageIdNumber,
              );
            }
          }
        }
        dispatch(setShowModal(false));
      }
    }

    setValidated(valid);
  };

  const modalHeaderTitleVariant = () => {
    const cardTypeMatch = staticCardTypes
      .concat(activityCardTypes)
      .find((item) => item.id === type);
    return cardTypeMatch?.name;
  };

  return card ? (
    <>
      <ContentEditorHeader
        modalHeaderClass={type}
        closeModalVariant="white"
        modalType="card"
        modalHeaderTitle={modalHeaderTitleVariant()}
        locationId="activity-introduction"
      />
      <Modal.Body className="content-editor-body p-0">
        <Form
          role="form"
          onSubmit={handleSubmit}
          validated={validated}
          noValidate
          className="card-modal-content unselectable"
        >
          <ErrorBoundary
            fallback={
              <div>
                <p>{_("Unable to load form")}</p>
              </div>
            }
          >
            <React.Suspense fallback={<div role="status"></div>}>
              <ActivityEditForm
                onChangeValidated={onChangeValidated}
                type={type}
                data={data}
                onChangeData={onChangeData}
                validated={validated}
              />
            </React.Suspense>
          </ErrorBoundary>
        </Form>
      </Modal.Body>
    </>
  ) : (
    <>
      <Modal.Body className="content-editor-body position-relative d-flex flex-column align-items-center p-5">
        <BLSpinner className="overflow-visible" size="3rem" />
        <p className="p-0 m-0 pt-3">{_("Loading")}...</p>
      </Modal.Body>
    </>
  );
};

export default AddActivity;
